import { Link, useParams, Navigate } from "react-router-dom";
import { Boxes, Megaphone, Code2, Cpu, ArrowRight, ArrowUpRight, Check, CircleHelp } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { PILLARS } from "../data/services";

const ICONS = { Boxes, Megaphone, Code2, Cpu };

export default function ServicePage() {
    const { slug } = useParams();
    const pillar = PILLARS.find((p) => p.slug === slug);

    if (!pillar) return <Navigate to="/" replace />;

    const Icon = ICONS[pillar.icon];
    const others = PILLARS.filter((p) => p.slug !== slug);

    return (
        <>
            <Seo title={pillar.name} description={pillar.summary} />

            <section className="bg-grid relative overflow-hidden py-20 lg:py-24" data-testid="service-hero">
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{ background: `radial-gradient(900px 480px at 20% 0%, ${pillar.accent}26, transparent 60%)` }}
                />
                <div className="container-x relative grid items-center gap-12 lg:grid-cols-2">
                    <Reveal>
                        <span
                            className="inline-flex h-14 w-14 items-center justify-center rounded-2xl"
                            style={{ background: pillar.accentSoft, color: pillar.accent }}
                        >
                            <Icon size={26} aria-hidden="true" />
                        </span>
                        <p className="eyebrow mt-6" style={{ color: pillar.accent }}>{pillar.tagline}</p>
                        <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">{pillar.name}</h1>
                        <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
                            {pillar.summary}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <Link to={`/contact?service=${pillar.slug}`} className="btn-primary" data-testid="service-enquire-button">
                                Enquire about {pillar.name}
                                <ArrowUpRight size={16} aria-hidden="true" />
                            </Link>
                            <Link to="/contact" className="btn-ghost" data-testid="service-talk-button">
                                Talk to Our Team
                            </Link>
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <div
                            className="relative overflow-hidden rounded-3xl border"
                            style={{ borderColor: `${pillar.accent}55`, boxShadow: `0 24px 80px -32px ${pillar.accent}66` }}
                        >
                            <img
                                src={pillar.image}
                                alt={pillar.imageAlt}
                                className="h-72 w-full object-cover sm:h-96"
                                loading="lazy"
                            />
                            <div
                                className="absolute inset-0"
                                style={{ background: `linear-gradient(160deg, transparent 40%, ${pillar.accent}33 100%)` }}
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="py-16 lg:py-24" data-testid="service-includes">
                <div className="container-x grid gap-12 lg:grid-cols-2">
                    <Reveal>
                        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">What the service includes</h2>
                        <ul className="focus-grid mt-8 space-y-4">
                            {pillar.includes.map((item) => (
                                <li
                                    key={item}
                                    className="focus-card glass flex items-start gap-3 rounded-2xl p-4"
                                    style={{ "--accent": pillar.accent, "--accent-soft": pillar.accentSoft }}
                                >
                                    <Check size={18} className="mt-0.5 shrink-0" style={{ color: pillar.accent }} aria-hidden="true" />
                                    <span className="text-sm leading-relaxed text-slate-300">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">Needs it can address</h2>
                        <ul className="focus-grid mt-8 space-y-4">
                            {pillar.needs.map((need) => (
                                <li
                                    key={need}
                                    className="focus-card glass flex items-start gap-3 rounded-2xl p-4"
                                    style={{ "--accent": pillar.accent, "--accent-soft": pillar.accentSoft }}
                                >
                                    <CircleHelp size={18} className="mt-0.5 shrink-0" style={{ color: pillar.accent }} aria-hidden="true" />
                                    <span className="text-sm leading-relaxed text-slate-300">{need}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>

                <div className="container-x mt-16">
                    <Reveal>
                        <h2 className="text-xl font-semibold sm:text-2xl">Core capabilities</h2>
                        <ul className="mt-6 flex flex-wrap gap-3" data-testid="service-capabilities">
                            {pillar.capabilities.map((c) => (
                                <li
                                    key={c}
                                    className="rounded-full border px-5 py-2.5 text-sm font-medium text-slate-200"
                                    style={{ borderColor: `${pillar.accent}55`, background: pillar.accentSoft }}
                                >
                                    {c}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>
            </section>

            <section
                className="border-y border-white/5 py-16 lg:py-24"
                style={{
                    background: `linear-gradient(135deg, ${pillar.accent}14 0%, rgba(20,27,45,0.55) 45%, ${pillar.accent}0D 100%)`,
                }}
                data-testid="service-engagement"
            >
                <div className="container-x">
                    <Reveal>
                        <p className="eyebrow" style={{ color: pillar.accent }}>How an engagement works</p>
                        <h2 className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl">
                            A typical {pillar.name.toLowerCase()} engagement.
                        </h2>
                    </Reveal>
                    <div className="focus-grid mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {pillar.engagement.map((s, i) => (
                            <Reveal key={s.title} delay={i * 0.08}>
                                <div
                                    className="focus-card glass relative h-full rounded-3xl p-6"
                                    style={{ "--accent": pillar.accent, "--accent-soft": pillar.accentSoft }}
                                    data-testid={`engagement-step-${i}`}
                                >
                                    <span
                                        className="text-3xl font-bold"
                                        style={{ fontFamily: "Outfit, sans-serif", color: "transparent", WebkitTextStroke: `1.5px ${pillar.accent}88` }}
                                    >
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <h3 className="mt-4 text-base font-semibold">{s.title}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24" data-testid="service-next">
                <div className="container-x">
                    <Reveal>
                        <div
                            className="glass flex flex-col items-start justify-between gap-8 rounded-3xl p-8 sm:p-12 lg:flex-row lg:items-center"
                            style={{ boxShadow: `0 24px 90px -40px ${pillar.accent}55` }}
                        >
                            <div>
                                <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                                    Ready to talk about {pillar.name.toLowerCase()}?
                                </h2>
                                <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-400">
                                    Tell us about your situation and we'll come back with honest, practical next
                                    steps — no obligation.
                                </p>
                            </div>
                            <Link to={`/contact?service=${pillar.slug}`} className="btn-primary shrink-0" data-testid="service-bottom-cta">
                                Start the conversation
                                <ArrowRight size={16} aria-hidden="true" />
                            </Link>
                        </div>
                    </Reveal>

                    <div className="mt-14">
                        <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500">Explore the other pillars</h3>
                        <div className="focus-grid mt-5 grid gap-4 sm:grid-cols-3">
                            {others.map((p) => (
                                <Link
                                    key={p.slug}
                                    to={`/services/${p.slug}`}
                                    data-testid={`service-related-${p.slug}`}
                                    className="focus-card glass group flex items-center justify-between rounded-2xl p-5"
                                    style={{ "--accent": p.accent, "--accent-soft": p.accentSoft }}
                                >
                                    <span className="text-sm font-semibold">{p.name}</span>
                                    <ArrowRight size={16} style={{ color: p.accent }} className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
